import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  GetGeneralLedgerCodes,
  DeleteGeneralLedgerCode,
} from "../../../services/BillingMaintenanceService";
import CommonLoader from "../../common/CommonLoader";
import ViewGeneralLedgerCodes from "./ViewGeneralLedgerCodes";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import AddEditGLCodeDialog from "./AddEditGLCodeDialog"; // Import the new AddEditGLCodeDialog component

const GeneralLedgerCodesTable = () => {
  const columns = ["GL Code", "GL Account Description", "Actions"];
  const columnHeaders = ["glCode", "glAccountDescription"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [glCodesData, setGlCodesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGlCode, setSelectedGlCode] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editGLCode, setEditGLCode] = useState(null);

  useEffect(() => {


    fetchGlCodes();
  }, []);

  useEffect(() => {
    const filteredResults = glCodesData.filter((glCode) =>
      columnHeaders.some((header) =>
        String(glCode[header]).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, glCodesData]);

  const fetchGlCodes = async () => {
    try {
      setLoading(true);
      const response = await GetGeneralLedgerCodes();
      setGlCodesData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching General Ledger Codes:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (glCode) => {
    setEditGLCode(glCode);
    setIsDialogOpen(true);
  };

  const handleAddClick = () => {
    setEditGLCode(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditGLCode(null);
  };
  
  const handleDialogSuccess = () => {
    fetchGlCodes();
  };

  const handleDeleteClick = (glCode) => {
    setSelectedItemToDelete(glCode);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteGeneralLedgerCode(selectedItemToDelete.glCode);
      setIsDeleteConfirmationOpen(false);
      const updatedGlCodes = glCodesData.filter(
        (item) => item.glCode !== selectedItemToDelete.glCode
      );
      setGlCodesData(updatedGlCodes);
      setFilteredData(updatedGlCodes);
      NotificationManager.success("General Ledger Code deleted successfully");
    } catch (error) {
      console.error("Error deleting General Ledger Code:", error);
      NotificationManager.error("Error deleting General Ledger Code");
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleViewClick = (glCode) => {
    setSelectedGlCode(glCode);
  };

  const handleBackClick = () => {
    setSelectedGlCode(null);
    setSearchTerm("");
  };

  const renderActions = (glCode) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(glCode)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(glCode)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(glCode)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">General Ledger Codes </span>
    <hr />
      {selectedGlCode ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewGeneralLedgerCodes glCode={selectedGlCode} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((glCode, index) => (
                      <TableRow
                        key={glCode.glCode}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>{glCode[column]}</TableCell>
                        ))}
                        <TableCell>{renderActions(glCode)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
        </>
      )}

      <AddEditGLCodeDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editGLCode={editGLCode}
      />
      {/* Render the delete confirmation popup */}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete the ${selectedItemToDelete?.glCode}?`}
        color="red"
      />
    </>
  );
};

export default GeneralLedgerCodesTable;
